import { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { listAgents } from '../api/agents'
import { listIndustries } from '../api/industries'
import type { Agent, IndustryListItem } from '../types'


// Internal nav links use react-router's <Link> rather than navigate-on-click
// buttons so the rendered HTML carries real <a href> attributes — required
// for non-JS crawlers (Google OAuth verification, etc.) to discover the
// footer URLs. Keeps the same display/hover styles as the previous buttons.
const linkStyle: React.CSSProperties = {
  display: 'block',
  background: 'none',
  border: 'none',
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  color: '#ffffff',
  padding: '4px 0',
  cursor: 'pointer',
  textAlign: 'center',
  textDecoration: 'none',
  transition: 'color 150ms',
}

const bottomLinkStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  color: 'var(--text-muted)',
  cursor: 'pointer',
  padding: 0,
  textDecoration: 'none',
  transition: 'color 150ms',
}

const headingStyle: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '11px',
  color: 'var(--accent-purple)',
  letterSpacing: '2px',
  textTransform: 'uppercase',
  marginBottom: '16px',
}

export default function Footer() {
  const navigate = useNavigate()
  const [agents, setAgents] = useState<Agent[]>([])
  const [industries, setIndustries] = useState<IndustryListItem[]>([])

  useEffect(() => {
    listAgents().then(setAgents).catch(() => {})
    listIndustries().then(setIndustries).catch(() => {})
  }, [])

  function scrollTo(id: string) {
    const el = document.getElementById(id)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' })
    } else {
      navigate(`/#${id}`)
    }
  }

  return (
    <footer role="contentinfo" style={{ background: 'var(--bg-surface)', borderTop: '1px solid var(--border-subtle)' }}>

      {/* ROW 1 — 4 columns */}
      <div className="footer-grid" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '40px',
        padding: '64px 80px 48px',
      }}>

        {/* Column 1 — Brand */}
        <div>
          <div onClick={() => navigate('/')} style={{ fontFamily: 'var(--font-display)', fontSize: '18px', fontWeight: 800, color: 'var(--text-primary)', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <img src="/owl-mascot.png" alt="Aspired AI" style={{ height: '24px', width: '24px', objectFit: 'contain', borderRadius: '4px', verticalAlign: 'middle', marginRight: '4px', display: 'inline-block' }} /> Aspired AI
          </div>
          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#ffffff', margin: 0 }}>
            AI agents built for small business.
          </p>
        </div>

        {/* Column 2 — Product */}
        <div>
          <div style={headingStyle}>PRODUCT</div>
          {[
            { label: 'Home', to: '/' },
            { label: 'Pricing', to: '/pricing' },
            { label: 'Compare', to: '/compare' },
            { label: 'Blog', to: '/blog' },
            { label: 'FAQ', to: '/faq' },
            { label: 'Sign In', to: '/login' },
            { label: 'Start Free', to: '/register' },
          ].map((item) => (
            <Link
              key={item.label}
              to={item.to}
              style={linkStyle}
              onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--accent-purple)')}
              onMouseLeave={(e) => (e.currentTarget.style.color = '#ffffff')}
            >
              {item.label}
            </Link>
          ))}
        </div>

        {/* Column 3 — Agents */}
        <div>
          <div style={headingStyle}>AGENTS</div>
          {agents.map((agent) => (
            <Link
              key={agent.id}
              to={`/${agent.id}`}
              style={linkStyle}
              onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--accent-purple)')}
              onMouseLeave={(e) => (e.currentTarget.style.color = '#ffffff')}
            >
              {agent.name} — {agent.role}
            </Link>
          ))}
        </div>

        {/* Column 4 — Industries */}
        <div>
          <div style={headingStyle}>INDUSTRIES</div>
          {industries.map((ind) => (
            <Link
              key={ind.slug}
              to={`/industries/${ind.slug}`}
              style={linkStyle}
              onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--accent-purple)')}
              onMouseLeave={(e) => (e.currentTarget.style.color = '#ffffff')}
            >
              {ind.name}
            </Link>
          ))}
        </div>

      </div>

      {/* ROW 2 — 1 column centered */}
      <div style={{
        borderTop: '1px solid var(--border-subtle)',
        padding: '24px 80px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '6px',
        textAlign: 'center',
      }}>
        <span style={{ fontFamily: 'Inter, sans-serif', color: 'var(--text-muted)', fontSize: '13px' }}>
          © 2026 Aspired AI. All rights reserved.
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {[
            { label: 'Privacy Policy', to: '/privacy' },
            { label: 'Trust & Data', to: '/trust' },
            { label: 'Terms of Service', to: '/terms' },
            { label: "What's New", to: '/whats-new' },
            { label: 'Contact', to: '/contact' },
          ].map((link, i, arr) => (
            <span key={link.label} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Link
                to={link.to}
                style={bottomLinkStyle}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#ffffff')}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-muted)')}
              >
                {link.label}
              </Link>
              {i < arr.length - 1 && <span style={{ color: 'var(--border-default)', fontSize: '12px' }}>·</span>}
            </span>
          ))}
        </div>
      </div>

    </footer>
  )
}
